import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useLaudos, useAllLaudos, useUsuarios } from "@/hooks/use-data";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelecionarTecnicoVistoria } from "@/components/laudos/SelecionarTecnicoVistoria";
import { SolicitarLaudoDialog } from "@/components/laudos/SolicitarLaudoDialog";
import { AguardandoCreditoBadge } from "@/components/laudos/AguardandoCreditoBadge";
import { FileText, Calendar, Plus, Building2, Users, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { LAUDO_STATUS_LABELS, LAUDO_STATUS_COLORS } from "@/lib/constants";
import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@shared/routes";
import { queryClient } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import type { LaudoWithDetails } from "@shared/schema";
import { useCreditosTransacoesPorEmpresa, CreditoTransacao } from "@/hooks/use-data";
import { rotuloCartela, tituloLaudo } from "@/lib/laudo-identificador";
import { useModulos } from "@/hooks/use-modulos";

export default function Laudos() {
  const { profile, session } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ownerVistoriadoraFilter, setOwnerVistoriadoraFilter] = useState<string>("all");
  const [ownerClienteFilter, setOwnerClienteFilter] = useState<string>("all");
  const [, navigate] = useLocation();
  const isOwner = profile?.roles?.includes("owner") || false;
  const isParceiro = profile?.roles?.includes("parceiro_negocios") || false;
  const empresaId = profile?.empresa?.id;
  const vistoriadoraId = isOwner || isParceiro ? undefined : empresaId;
  const parceiroId = isParceiro ? empresaId : undefined;
  const [editingLaudoId, setEditingLaudoId] = useState<string | null>(null);
  const [showSolicitar, setShowSolicitar] = useState(false);

  const { modulos } = useModulos();
  const creditosAtivos = isParceiro && modulos.creditosPrePagos;

  const {
    data: laudos,
    isLoading,
    refetch,
  } = isOwner
    ? useAllLaudos(statusFilter !== "all" ? statusFilter : undefined)
    : useLaudos(vistoriadoraId, statusFilter !== "all" ? statusFilter : undefined, undefined, undefined, parceiroId);

  const { data: transacoes } = useCreditosTransacoesPorEmpresa(creditosAtivos ? empresaId : undefined);

  const transacoesPorLaudo = useMemo<Map<string, CreditoTransacao>>(() => {
    if (!transacoes || !creditosAtivos) return new Map();
    return new Map(
      transacoes.filter((t) => !!t.laudo_id).map((t) => [t.laudo_id!, t]),
    );
  }, [transacoes, creditosAtivos]);

  const { data: usuarios, isLoading: usuariosLoading } = useUsuarios(empresaId);
  const tecnicos = usuarios?.filter((u) => u.roles?.includes("tv")) || [];

  const isAdmin = profile?.roles?.includes("admin") || false;
  const isResponsavelTecnico = profile?.roles?.includes("rt") || false;
  const isCliente = profile?.roles?.includes("parceiro_negocios") || false;

  const canSelectTecnico = isAdmin || isResponsavelTecnico || isOwner;
  const canSolicitar = isCliente || isAdmin || isResponsavelTecnico || isOwner;

  // Derivar opções de filtro (apenas para owner) a partir dos laudos carregados
  const vistoriadorasOptions = useMemo(() => {
    if (!isOwner || !laudos) return [];
    const seen = new Map<string, string>();
    for (const l of laudos) {
      if (l.vistoriadoraId && !seen.has(l.vistoriadoraId)) {
        const nome =
          (l as any).vistoriadora?.nomeFantasia ||
          (l as any).vistoriadora?.razaoSocial ||
          l.vistoriadoraId;
        seen.set(l.vistoriadoraId, nome);
      }
    }
    return Array.from(seen.entries()).map(([id, nome]) => ({ id, nome }));
  }, [isOwner, laudos]);

  const clientesOptions = useMemo(() => {
    if (!isOwner || !laudos) return [];
    const seen = new Map<string, string>();
    for (const l of laudos) {
      if (l.parceiroId && !seen.has(l.parceiroId)) {
        const nome =
          (l as any).parceiro?.nomeFantasia ||
          (l as any).parceiro?.razaoSocial ||
          l.parceiroId;
        seen.set(l.parceiroId, nome);
      }
    }
    return Array.from(seen.entries()).map(([id, nome]) => ({ id, nome }));
  }, [isOwner, laudos]);

  // Aplicar filtros de owner client-side + ordenar por mais recente
  const laudosFiltrados = useMemo(() => {
    let list = !isOwner ? (laudos ?? []) : (laudos ?? []).filter((l) => {
      if (ownerVistoriadoraFilter !== "all" && l.vistoriadoraId !== ownerVistoriadoraFilter) return false;
      if (ownerClienteFilter !== "all" && l.parceiroId !== ownerClienteFilter) return false;
      return true;
    });
    return [...list].sort((a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime());
  }, [isOwner, laudos, ownerVistoriadoraFilter, ownerClienteFilter]);

  // Owner sem filtro: vistoriadora → parceiro (dois níveis)
  const laudosPorVistoriadora = useMemo(() => {
    if (!isOwner || ownerVistoriadoraFilter !== "all") return null;
    const grupos = new Map<string, { nome: string; porParceiro: Map<string, { nome: string; laudos: LaudoWithDetails[] }> }>();
    for (const l of laudosFiltrados) {
      const vid = l.vistoriadoraId ?? "sem_vistoriadora";
      const vnome = (l as any).vistoriadora?.nomeFantasia || (l as any).vistoriadora?.razaoSocial || "Vistoriadora desconhecida";
      if (!grupos.has(vid)) grupos.set(vid, { nome: vnome, porParceiro: new Map() });
      const pid = l.parceiroId ?? "sem_parceiro";
      const pnome = (l as any).parceiro?.nomeFantasia || (l as any).parceiro?.razaoSocial || "Sem parceiro";
      const vg = grupos.get(vid)!;
      if (!vg.porParceiro.has(pid)) vg.porParceiro.set(pid, { nome: pnome, laudos: [] });
      vg.porParceiro.get(pid)!.laudos.push(l);
    }
    return grupos;
  }, [isOwner, ownerVistoriadoraFilter, laudosFiltrados]);

  // Vistoriadora ou owner com filtro de vistoriadora: agrupa por parceiro
  const laudosPorParceiro = useMemo(() => {
    if (isParceiro) return null;
    if (isOwner && ownerVistoriadoraFilter === "all") return null;
    const grupos = new Map<string, { nome: string; laudos: LaudoWithDetails[] }>();
    for (const l of laudosFiltrados) {
      const pid = l.parceiroId ?? "sem_parceiro";
      const nome = (l as any).parceiro?.nomeFantasia || (l as any).parceiro?.razaoSocial || "Sem parceiro";
      if (!grupos.has(pid)) grupos.set(pid, { nome, laudos: [] });
      grupos.get(pid)!.laudos.push(l);
    }
    return grupos;
  }, [isOwner, isParceiro, ownerVistoriadoraFilter, laudosFiltrados]);

  const mutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const res = await fetch(api.laudos.update.path.replace(":id", id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})); // 502 de proxy vem em HTML
        throw new Error(err.message || "Erro ao atualizar laudo");
      }
      return res.json();
    },
    onSuccess: () => {
      refetch();
      toast({ title: "Laudo atualizado com sucesso." });
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const handleSelectTecnico = async (laudo: LaudoWithDetails, tecnico: any) => {
    setEditingLaudoId(laudo.id);
    try {
      await mutation.mutateAsync({
        id: laudo.id,
        tecnicoVistoriaId: tecnico.id,
      });
    } finally {
      setEditingLaudoId(null);
    }
  };

  return (
    <Shell>
      <SolicitarLaudoDialog open={showSolicitar} onClose={() => setShowSolicitar(false)} />

      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold font-display text-slate-900" data-testid="text-page-title">
            Laudos Técnicos
          </h1>
          <p className="text-muted-foreground mt-1">Acompanhe laudos de vistoria</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSolicitar && (
            <Button onClick={() => setShowSolicitar(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Solicitar Laudo
            </Button>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-status-filter">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              {Object.entries(LAUDO_STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filtros exclusivos do owner */}
      {isOwner && (
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-400" />
            <Select
              value={ownerVistoriadoraFilter}
              onValueChange={(v) => {
                setOwnerVistoriadoraFilter(v);
                setOwnerClienteFilter("all");
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Todas as vistoriadoras" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as vistoriadoras</SelectItem>
                {vistoriadorasOptions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <Select
              value={ownerClienteFilter}
              onValueChange={(v) => {
                setOwnerClienteFilter(v);
                if (v !== "all") setOwnerVistoriadoraFilter("all");
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clientesOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(ownerVistoriadoraFilter !== "all" || ownerClienteFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setOwnerVistoriadoraFilter("all"); setOwnerClienteFilter("all"); }}
            >
              Limpar filtros
            </Button>
          )}
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-4">
          {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : laudosFiltrados.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          Nenhum laudo encontrado
        </div>
      ) : laudosPorVistoriadora ? (
        // Owner sem filtro: vistoriadora → parceiro
        <div className="space-y-10">
          {Array.from(laudosPorVistoriadora.entries()).map(([vid, vGrupo]) => {
            const totalV = Array.from(vGrupo.porParceiro.values()).reduce((s, pg) => s + pg.laudos.length, 0);
            return (
              <div key={vid}>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  <h2 className="text-base font-semibold text-slate-800">{vGrupo.nome}</h2>
                  <span className="text-xs text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-full">
                    {totalV} laudo{totalV !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="space-y-6 pl-4 border-l-2 border-slate-100">
                  {Array.from(vGrupo.porParceiro.entries()).map(([pid, pGrupo]) => {
                    const badge = pGrupo.laudos[0] ? badgeMaisRecente(pGrupo.laudos[0].criadoEm) : null;
                    return (
                    <div key={pid}>
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <h3 className="text-sm font-medium text-slate-600">{pGrupo.nome}</h3>
                        <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
                          {pGrupo.laudos.length}
                        </span>
                        {badge && (
                          <span className={cn("text-xs font-medium px-1.5 py-0.5 rounded-full", badge.novo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500")}>
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <div className="space-y-3">
                        {pGrupo.laudos.map((laudo) => (
                          <LaudoCard
                            key={laudo.id}
                            laudo={laudo}
                            canSelectTecnico={canSelectTecnico}
                            tecnicos={tecnicos}
                            editingLaudoId={editingLaudoId}
                            onSelectTecnico={handleSelectTecnico}
                            onNavigate={() => navigate(`/laudos/${laudo.id}`)}
                            transacao={transacoesPorLaudo.get(laudo.id)}
                          />
                        ))}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : laudosPorParceiro ? (
        // Vistoriadora ou owner com filtro de vistoriadora: agrupa por parceiro
        <div className="space-y-8">
          {Array.from(laudosPorParceiro.entries()).map(([pid, grupo]) => {
            const badge = grupo.laudos[0] ? badgeMaisRecente(grupo.laudos[0].criadoEm) : null;
            return (
            <div key={pid}>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-blue-500" />
                <h2 className="text-base font-semibold text-slate-800">{grupo.nome}</h2>
                <span className="text-xs text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-full">
                  {grupo.laudos.length} laudo{grupo.laudos.length !== 1 ? "s" : ""}
                </span>
                {badge && (
                  <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", badge.novo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500")}>
                    {badge.label}
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {grupo.laudos.map((laudo) => (
                  <LaudoCard
                    key={laudo.id}
                    laudo={laudo}
                    canSelectTecnico={canSelectTecnico}
                    tecnicos={tecnicos}
                    editingLaudoId={editingLaudoId}
                    onSelectTecnico={handleSelectTecnico}
                    onNavigate={() => navigate(`/laudos/${laudo.id}`)}
                    transacao={transacoesPorLaudo.get(laudo.id)}
                  />
                ))}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        // Lista plana — apenas parceiro de negócios
        <div className="space-y-3">
          {laudosFiltrados.map((laudo) => (
            <LaudoCard
              key={laudo.id}
              laudo={laudo}
              canSelectTecnico={canSelectTecnico}
              tecnicos={tecnicos}
              editingLaudoId={editingLaudoId}
              onSelectTecnico={handleSelectTecnico}
              onNavigate={() => navigate(`/laudos/${laudo.id}`)}
              transacao={transacoesPorLaudo.get(laudo.id)}
            />
          ))}
        </div>
      )}
    </Shell>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function badgeMaisRecente(date: Date | string): { label: string; novo: boolean } {
  const h = (Date.now() - new Date(date).getTime()) / 3_600_000;
  if (h < 24) return { label: "Novo", novo: true };
  if (h < 48) return { label: "Ontem", novo: false };
  return { label: new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }), novo: false };
}

// ─── Componente reutilizável do card de laudo ─────────────────────────────────

const VEICULO_EMOJI: Record<string, string> = {
  auto_leve: "🚗",
  moto: "🏍️",
  caminhao: "🚛",
  onibus: "🚌",
};

export function LaudoCard({
  laudo,
  canSelectTecnico,
  tecnicos,
  editingLaudoId,
  onSelectTecnico,
  onNavigate,
  transacao,
}: {
  laudo: LaudoWithDetails;
  canSelectTecnico: boolean;
  tecnicos: any[];
  editingLaudoId: string | null;
  onSelectTecnico: (laudo: LaudoWithDetails, tecnico: any) => void | Promise<void>;
  onNavigate: () => void;
  transacao?: CreditoTransacao;
}) {
  const laudoNaoVisitoriado = !laudo.tecnicoVistoriaId;
  const parceiroNome =
    (laudo as any).parceiro?.nomeFantasia ||
    (laudo as any).parceiro?.razaoSocial;

  return (
    <Card className="border-none shadow-md hover:shadow-lg transition-shadow" data-testid={`card-laudo-${laudo.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div
            className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 cursor-pointer"
            onClick={onNavigate}
          >
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onNavigate}>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-slate-900">
                {tituloLaudo(laudo)}
              </h3>
              <Badge
                variant="secondary"
                className={cn("text-xs", LAUDO_STATUS_COLORS[laudo.status])}
              >
                {LAUDO_STATUS_LABELS[laudo.status] || laudo.status}
              </Badge>
              <AguardandoCreditoBadge laudo={laudo} />
              {transacao && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  <Wallet className="w-3 h-3" />
                  {transacao.quantidade} crédito
                  {transacao.tipo_veiculo && ` · ${VEICULO_EMOJI[transacao.tipo_veiculo] ?? ""}`}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-slate-500">
              {laudo.veiculo && (
                <span>{laudo.veiculo.marca} {laudo.veiculo.modelo} - {laudo.veiculo.placa}</span>
              )}
              {rotuloCartela(laudo.cartela) && <span>{rotuloCartela(laudo.cartela)}</span>}
              {parceiroNome && (
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {parceiroNome}
                </span>
              )}
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(laudo.criadoEm).toLocaleDateString("pt-BR")}
              </div>
            </div>
            {laudo.laudoPecas && laudo.laudoPecas.filter((lp) => lp.fotoUrl).length > 0 && (
              <p className="text-xs text-slate-400 mt-1">
                {laudo.laudoPecas.filter((lp) => lp.fotoUrl).length} foto(s)
              </p>
            )}
          </div>
          <div className="text-right text-sm text-slate-500">
            {laudo.dataVistoria && (
              <p>Vistoria: {new Date(laudo.dataVistoria).toLocaleDateString("pt-BR")}</p>
            )}
            {laudo.dataAssinatura && (
              <p>Assinado: {new Date(laudo.dataAssinatura).toLocaleDateString("pt-BR")}</p>
            )}
          </div>
          {canSelectTecnico && laudoNaoVisitoriado && (
            <SelecionarTecnicoVistoria
              tecnicosDisponiveis={tecnicos}
              tecnicoAtual={laudo.tecnicoVistoria}
              laudoNaoVisitoriado={laudoNaoVisitoriado}
              isLoading={editingLaudoId === laudo.id}
              exigeFotosVeiculo={laudo.moduloDetranVeiculoFoto}
              veiculoId={laudo.veiculoId}
              onSelect={async (tecnico) => { await onSelectTecnico(laudo, tecnico); }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
